import type {
  SxSummaryFixture,
  SxSummaryLeague,
  SxSummarySport,
} from "../types/markets"
import { SX_CONFIG } from "../constants"
import { calculateOdds } from "../hooks/useSXMarketData"
import { sports } from "../constants/sports"
import { isAfter48Hours } from "@/utils/helper"

/**
 * Filter out locked markets (games after end of tomorrow) and live games
 */
export const filterLockedMarkets = (fixture: SxSummaryFixture): boolean => {
  const now = new Date()
  const endOfTomorrow = new Date(now)
  endOfTomorrow.setDate(now.getDate() + 1)
  endOfTomorrow.setHours(23, 59, 59, 999)
  const endOfTomorrowTime = endOfTomorrow.getTime()
  const isLocked = +new Date(fixture.gameTime).getTime() > endOfTomorrowTime

  // Also filter out live games as they are not active for betting
  const isLive = isFixtureLive(fixture)

  return !isLocked && !isLive
}

/**
 * Transform SX.bet active markets into sports structure with live parameters
 */
export const transformSxActiveMarkets = (
  sports: SxSummarySport[],
  chainId?: number
): any[] => {
  if (!sports || sports.length === 0) {
    return []
  }

  // Transform each sport
  const transformedSports = sports
    .map(sport => {
      // Transform leagues within the sport
      const transformedLeagues = sport.leagues
        .map(league => {
          // Merge fixtures with the same id coming from different bet groups
          const mergedFixturesMap = new Map<string, any>()
          for (const fixture of league.fixtures || []) {
            const key = String(fixture.id)
            const existing = mergedFixturesMap.get(key)
            if (existing) {
              const combinedMarkets = [
                ...(existing.markets || []),
                ...(fixture.markets || []),
              ]
              const uniqueMarketsMap = new Map(
                combinedMarkets
                  .filter(m => m && m.marketHash)
                  .map(m => [m.marketHash, m])
              )
              existing.markets = Array.from(uniqueMarketsMap.values())
              const existingBetGroups = Array.isArray(existing.betGroups)
                ? existing.betGroups
                : []
              const fixtureBetGroups = Array.isArray(fixture.betGroups)
                ? fixture.betGroups
                : []
              existing.betGroups = Array.from(
                new Set([
                  ...(existingBetGroups || []),
                  ...(fixtureBetGroups || []),
                ])
              )
              const existingMarketHashes = Array.isArray(existing.marketHashes)
                ? existing.marketHashes
                : []
              const fixtureMarketHashes = Array.isArray(fixture.marketHashes)
                ? fixture.marketHashes
                : []
              existing.marketHashes = Array.from(
                new Set([
                  ...(existingMarketHashes || []),
                  ...(fixtureMarketHashes || []),
                ])
              )
              if (fixture.volume && !existing.volume) {
                existing.volume = fixture.volume
              }
              mergedFixturesMap.set(key, existing)
            } else {
              mergedFixturesMap.set(key, { ...fixture })
            }
          }

          const mergedFixtures = Array.from(mergedFixturesMap.values())

          const validFixtures = mergedFixtures.filter(fixture => {
            // Filter out fixtures that start after tomorrow
            if (isAfter48Hours(fixture.gameTime)) {
              return false
            }

            return (
              fixture.markets &&
              fixture.markets.some(
                market => market.bestOddsOutcomeOne && market.bestOddsOutcomeTwo
              )
            )
          })

          // Skip league if no valid fixtures
          if (validFixtures.length === 0) {
            return null
          }

          // Transform games/fixtures within the league
          const games = validFixtures.map(fixture => {
            const gameTime = new Date(fixture.gameTime).getTime() / 1000

            // Create game object (no live games since they're filtered out)
            return {
              gameId: fixture.id,
              title: `${fixture.teamOne} - ${fixture.teamTwo}`,
              homeTeam: fixture.teamOne,
              awayTeam: fixture.teamTwo,
              startsAt: gameTime,
              // No live properties since live games are filtered out
              score: null,
              isLive: false,
              liveInfo: null,
              participants: [
                {
                  name: fixture.teamOne,
                  image: undefined,
                  id: fixture.id + "_team1",
                  score: undefined,
                },
                {
                  name: fixture.teamTwo,
                  image: undefined,
                  id: fixture.id + "_team2",
                  score: undefined,
                },
              ],
              league: {
                id: league.leagueId,
                name: league.leagueLabel,
                slug: league.leagueLabel.toLowerCase().replace(/\s+/g, "-"),
                tournamentName: league.leagueLabel,
              },
              sport: {
                sportId: sport.sportId,
                name: getSportName(sport.sportId),
                slug: getSportSlug(sport.sportId),
              },
              country: {
                name: "International",
                slug: "international",
              },
              markets: fixture.markets
                .filter(
                  market =>
                    market.bestOddsOutcomeOne && market.bestOddsOutcomeTwo
                )
                .map(market => ({
                  marketHash: market.marketHash,
                  outcomeOneName: market.outcomeOneName,
                  outcomeTwoName: market.outcomeTwoName,
                  bestOddsOutcomeOne: market.bestOddsOutcomeOne,
                  bestOddsOutcomeTwo: market.bestOddsOutcomeTwo,
                  marketType: market.marketType,
                  betGroup: market.betGroup,
                  status: fixture.status,
                  chainVersion: fixture.chainVersion,
                  isMainLine: true, // Default to true for main markets
                })),
              // Only prematch games since live games are filtered out
              liveEnabled: false,
              state: "prematch",
              status: fixture.status,
              chainVersion: fixture.chainVersion,
              volume: fixture.volume,
              // Additional metadata
              betGroups: fixture.betGroups,
              marketHashes: fixture.marketHashes,
            }
          })

          // Return transformed league
          return {
            id: league.leagueId,
            name: league.leagueLabel,
            slug: league.leagueLabel.toLowerCase().replace(/\s+/g, "-"),
            countrySlug: "international",
            countryName: "International",
            games,
          }
        })
        .filter(league => league !== null) // Remove null leagues

      // Return transformed sport
      return {
        id: sport.sportId,
        sportId: sport.sportId,
        name: getSportName(sport.sportId),
        slug: getSportSlug(sport.sportId),
        turnover: "0",
        tournamentsCount: sport.leagues.length,
        countriesCount: 1,
        leagues: transformedLeagues,
        hasLiveGames: false, // No live games since they're filtered out
      }
    })
    .filter(sport => sport.leagues.length > 0) // Filter out sports with no valid leagues

  return transformedSports
}

/**
 * Transform SX markets into the format expected by SXOutcomeButton
 * This matches the pattern used in useSXPopularMarkets.ts
 */
export const transformSxMarketsForComponents = (
  sports: SxSummarySport[],
  chainId: number,
  isSXChain: boolean
): any[] => {
  if (!sports || sports.length === 0) {
    return []
  }

  const allMarkets: any[] = []

  // Collect fixtures and extract markets
  sports.forEach(sport => {
    sport.leagues.forEach(league => {
      // Merge fixtures with the same id coming from different bet groups
      const mergedFixturesMap = new Map<string, any>()
      for (const fixture of league.fixtures || []) {
        const key = String(fixture.id)
        const existing = mergedFixturesMap.get(key)
        if (existing) {
          const combinedMarkets = [
            ...(existing.markets || []),
            ...(fixture.markets || []),
          ]
          const uniqueMarketsMap = new Map(
            combinedMarkets
              .filter(m => m && m.marketHash)
              .map(m => [m.marketHash, m])
          )
          existing.markets = Array.from(uniqueMarketsMap.values())
          const existingBetGroups = Array.isArray(existing.betGroups)
            ? existing.betGroups
            : []
          const fixtureBetGroups = Array.isArray(fixture.betGroups)
            ? fixture.betGroups
            : []
          existing.betGroups = Array.from(
            new Set([...(existingBetGroups || []), ...(fixtureBetGroups || [])])
          )
          const existingMarketHashes = Array.isArray(existing.marketHashes)
            ? existing.marketHashes
            : []
          const fixtureMarketHashes = Array.isArray(fixture.marketHashes)
            ? fixture.marketHashes
            : []
          existing.marketHashes = Array.from(
            new Set([
              ...(existingMarketHashes || []),
              ...(fixtureMarketHashes || []),
            ])
          )
          mergedFixturesMap.set(key, existing)
        } else {
          mergedFixturesMap.set(key, { ...fixture })
        }
      }

      const mergedFixtures = Array.from(mergedFixturesMap.values())

      // Filter fixtures that have at least one market with valid odds
      const validFixtures = mergedFixtures.filter(fixture => {
        return (
          fixture.markets &&
          fixture.markets.some(
            market => market.bestOddsOutcomeOne && market.bestOddsOutcomeTwo
          )
        )
      })

      validFixtures.forEach(fixture => {
        // Extract all markets from fixtures
        const [foundGroup] = fixture.betGroups

        fixture.markets.forEach(market => {
          if (!market.bestOddsOutcomeOne || !market.bestOddsOutcomeTwo) {
            return // skip markets with null odds
          }

          const sportsSlug = getSportSlug(sport.sportId)
          const sportsName = getSportName(sport.sportId)

          allMarkets.push({
            sportId: sport.sportId,
            leagueLabel: league.leagueLabel,
            sportsSlug,
            sportsName,
            betGroup: foundGroup,
            marketType: market.marketType,
            outcomeOneName: market.outcomeOneName,
            outcomeTwoName: market.outcomeTwoName,
            providerMarketType: market.marketType,
            gameTime: fixture.gameTime,
            fixtureId: fixture.id,
            teamOne: fixture.teamOne,
            teamTwo: fixture.teamTwo,
            // No live game data since live games are filtered out
            score: null,
            isLive: false,
            liveInfo: null,
            market: {
              marketHash: market.marketHash,
              baseToken: isSXChain
                ? SX_CONFIG[chainId].CONTRACTS.TOKEN
                : "0x0000000000000000000000000000000000000000",
              bestOddsOutcomeOne: market.bestOddsOutcomeOne,
              bestOddsOutcomeTwo: market.bestOddsOutcomeTwo,
              outcomeOneOdds: market.bestOddsOutcomeOne
                ? calculateOdds(Number(market.bestOddsOutcomeOne))
                : null,
              outcomeTwoOdds: market.bestOddsOutcomeTwo
                ? calculateOdds(Number(market.bestOddsOutcomeTwo))
                : null,
            },
          })
        })
      })
    })
  })

  return allMarkets
}

/**
 * Transform SX game data into the format expected by SxMarkets component
 * This handles the conversion from the game.json structure to SXOutcomeButton-compatible data
 */
export const transformSxGameForMarkets = (game: any, chainId: number): any => {
  if (!game || !game.markets || !Array.isArray(game.markets)) {
    return {
      marketsByType: {},
    }
  }

  // Group markets by marketType
  const marketsByType: Record<string, any[]> = {}

  game.markets.forEach((market: any) => {
    // Skip markets that don't have the required properties or valid odds
    if (
      !market.marketHash ||
      !market.bestOddsOutcomeOne ||
      !market.bestOddsOutcomeTwo
    ) {
      console.warn("Skipping market with missing data or invalid odds:", market)
      return
    }

    const marketType = market.marketType || "Unknown"
    if (!marketsByType[marketType]) {
      marketsByType[marketType] = []
    }
    marketsByType[marketType].push(market)
  })

  // Return early if no valid markets found
  if (Object.keys(marketsByType).length === 0) {
    return {
      processedMarkets: [],
      originalMarkets: game.markets || [],
    }
  }

  // Convert to array format expected by Content component
  const processedMarkets = Object.entries(marketsByType).map(
    ([marketName, marketList]) => ({
      marketName,
      orders: marketList.flatMap(market => [
        {
          key: `${market.marketHash}-one`,
          label: market.outcomeOneName,
          odds: market.bestOddsOutcomeTwo
            ? calculateOdds(Number(market.bestOddsOutcomeTwo))
            : null,
          order: {
            ...game,
            outcomeOneName: market.outcomeOneName,
            outcomeTwoName: market.outcomeTwoName,
            marketType: market.marketType,
            market: {
              marketHash: market.marketHash,
              baseToken: SX_CONFIG[chainId].CONTRACTS.TOKEN, // Default token
              bestOddsOutcomeOne: market.bestOddsOutcomeOne,
              bestOddsOutcomeTwo: market.bestOddsOutcomeTwo,
              outcomeOneOdds: market.bestOddsOutcomeTwo
                ? calculateOdds(Number(market.bestOddsOutcomeTwo))
                : null,
              outcomeTwoOdds: market.bestOddsOutcomeTwo
                ? calculateOdds(Number(market.bestOddsOutcomeTwo))
                : null,
            },
          },
          outcome: "one",
        },
        {
          key: `${market.marketHash}-two`,
          label: market.outcomeTwoName,
          odds: market.bestOddsOutcomeOne
            ? calculateOdds(Number(market.bestOddsOutcomeOne))
            : null,
          order: {
            ...game,
            outcomeOneName: market.outcomeOneName,
            outcomeTwoName: market.outcomeTwoName,
            marketType: market.marketType,
            market: {
              marketHash: market.marketHash,
              baseToken: SX_CONFIG[chainId].CONTRACTS.TOKEN, // Default token
              bestOddsOutcomeOne: market.bestOddsOutcomeOne,
              bestOddsOutcomeTwo: market.bestOddsOutcomeTwo,
              outcomeOneOdds: market.bestOddsOutcomeTwo
                ? calculateOdds(Number(market.bestOddsOutcomeTwo))
                : null,
              outcomeTwoOdds: market.bestOddsOutcomeOne
                ? calculateOdds(Number(market.bestOddsOutcomeOne))
                : null,
            },
          },
          outcome: "two",
        },
      ]),
    })
  )

  return {
    processedMarkets,
    originalMarkets: game.markets,
  }
}

/**
 * Transform SX fixture into game info structure
 */
export const transformSxFixtureToGameInfo = (
  fixture: SxSummaryFixture,
  league: SxSummaryLeague,
  sport: SxSummarySport
) => {
  if (!fixture) {
    return null
  }

  const gameTime = new Date(fixture.gameTime).getTime() / 1000

  return {
    id: fixture.id,
    gameId: fixture.id,
    slug: createSlug(`${fixture.teamOne}-${fixture.teamTwo}-${fixture.id}`),
    title: `${fixture.teamOne} - ${fixture.teamTwo}`,
    startsAt: gameTime,
    turnover: fixture.volume.toString(),
    // No live game properties since live games are filtered out
    score: null,
    isLive: false,
    liveInfo: null,
    sport: {
      sportId: sport.sportId,
      name: getSportName(sport.sportId),
      slug: getSportSlug(sport.sportId),
    },
    league: {
      id: league.leagueId,
      name: league.leagueLabel,
      slug: league.leagueLabel.toLowerCase().replace(/\s+/g, "-"),
      tournamentName: league.leagueLabel,
    },
    country: {
      name: "International",
      slug: "international",
    },
    participants: [
      {
        name: fixture.teamOne,
        image: undefined,
        id: fixture.id + "_team1",
        score: undefined, // No live scores since live games are filtered out
      },
      {
        name: fixture.teamTwo,
        image: undefined,
        id: fixture.id + "_team2",
        score: undefined, // No live scores since live games are filtered out
      },
    ],
    markets: fixture.markets
      .filter(market => market.bestOddsOutcomeOne && market.bestOddsOutcomeTwo)
      .map(market => ({
        marketHash: market.marketHash,
        outcomeOneName: market.outcomeOneName,
        outcomeTwoName: market.outcomeTwoName,
        bestOddsOutcomeOne: market.bestOddsOutcomeOne,
        bestOddsOutcomeTwo: market.bestOddsOutcomeTwo,
        marketType: market.marketType,
        betGroup: market.betGroup,
        status: fixture.status,
        chainVersion: fixture.chainVersion,
        isMainLine: true,
      })),
    liveEnabled: false, // No live games since they're filtered out
    state: "prematch", // Only prematch games since live games are filtered out
    status: fixture.status,
    chainVersion: fixture.chainVersion,
    betGroups: fixture.betGroups,
    marketHashes: fixture.marketHashes,
  }
}

/**
 * Creates a URL-friendly slug from text
 */
export const createSlug = (text: string) => {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")
}

export const getSportName = (sportId: number) => {
  const originalName = sports.find(s => s.sportId === sportId)?.label

  if (!originalName) {
    return undefined
  }

  const sportNameMapping: Record<string, string> = {
    Soccer: "Football",
    Football: "American Football",
  }

  return sportNameMapping[originalName] || originalName
}

export const getSportSlug = (sportId: number) => {
  return sports.find(s => s.sportId === sportId)?.slug
}
/**
 * Check if a fixture is currently live
 */
export const isFixtureLive = (fixture: SxSummaryFixture): boolean => {
  // Check if fixture has score data (more reliable indicator for live games)
  if (fixture.score) {
    return true
  }
  // Fallback to time-based check
  const gameTime = new Date(fixture.gameTime).getTime() / 1000
  return gameTime < Date.now() / 1000
}

/**
 * Format live game time from period time (in seconds)
 */
export const formatLiveTime = (
  periodTime: string,
  currentPeriod: string
): string => {
  const timeInSeconds = parseInt(periodTime)
  const minutes = Math.floor(timeInSeconds / 60)
  const seconds = timeInSeconds % 60

  // For soccer, show minutes:seconds
  if (currentPeriod.includes("Half")) {
    return `${minutes}'`
  }

  // For other sports, show minutes:seconds
  return `${minutes}:${seconds.toString().padStart(2, "0")}`
}

/**
 * Get live game display text
 */
export const getLiveGameDisplayText = (fixture: any): string => {
  if (!fixture.score) {
    return ""
  }

  const { teamOneScore, teamTwoScore, currentPeriod, periodTime } =
    fixture.score
  const timeDisplay = formatLiveTime(periodTime, currentPeriod)

  return `${teamOneScore}-${teamTwoScore} • ${currentPeriod} ${timeDisplay}`
}

/**
 * Get live status indicator
 */
export const getLiveStatusIndicator = (
  fixture: any
): { isLive: boolean; status: string; time: string } => {
  if (!fixture.score) {
    return { isLive: false, status: "prematch", time: "" }
  }

  const { currentPeriod, periodTime } = fixture.score
  const timeDisplay = formatLiveTime(periodTime, currentPeriod)

  return {
    isLive: true,
    status: "live",
    time: `${currentPeriod} ${timeDisplay}`,
  }
}

/**
 * Get market type label based on market type
 */
export const getMarketTypeLabel = (type: string): string => {
  const marketTypes: Record<string, string> = {
    MONEY_LINE: "Money Line",
    SPREAD: "Handicap",
    OVER_UNDER: "Over/Under",
    TIE_1X2: "Tie",
    TEAM_ONE_1X2: "Team One",
    TEAM_TWO_1X2: "Team Two",
  }
  return marketTypes[type] || type
}

/**
 * Get market category based on market type
 */
export const getMarketCategory = (type: string): string => {
  const categories: Record<string, string> = {
    MONEY_LINE: "moneyline",
    SPREAD: "handicaps",
    OVER_UNDER: "totals",
    TIE_1X2: "1x2",
    TEAM_ONE_1X2: "1x2",
    TEAM_TWO_1X2: "1x2",
  }
  return categories[type] || "other"
}
